// import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

import LoaderWithBackdrop from '@/components/custom/LoaderWithBackdrop';
import { useNavigate, useParams } from "react-router-dom";


import ErrorPopup from '@/components/custom/InfoPopups/ErrorPopup';
import SuccessPopup from '@/components/custom/InfoPopups/SuccessPopup';
import { Button } from '@/components/ui/button';
import {
    Menubar,
    MenubarContent,
    MenubarItem,
    MenubarMenu,
    MenubarTrigger
} from "@/components/ui/menubar";

import { ClipboardPen, FileChartColumn, FileQuestionMark, MailPlus, Pencil, Settings, SquareKanban, Trash2 } from "lucide-react";

import { useDeleteOrganization } from "@/features/organization/hooks/useMutation/useDeleteOrganization";
import { useOrganizationStore } from "@/lib/zustand/useStore";

import { UnderlineLink } from "@/components/atoms/UnderlineLink/UnderlineLink";
import { ConfirmByTypingTextDialog } from '@/components/organisms/ConfirmByTypingTextDialog/ConfirmByTypingTextDialog';
import { OrganizationTypeWordingResolver } from '@/features/organization/components/atoms/OrganizationTypeWordingResolver';
import { EditOrganizationModal } from '@/features/organization/components/organisms/EditOrganizationModal';
import { OrganizationFormSchemaType } from '@/features/organization/components/organisms/OrganizationForm';
import { useUpdateOrganization } from '@/features/organization/hooks/useMutation/useUpdateOrganization';
import { useIsEditOrganizationModalOpen } from '@/features/organization/hooks/utils/useIsEditOrganizationModalOpen';
import { CheckOrganizationAccess } from '@/features/rbac/components/molecules/CheckOrganizationAccess';


export const OrganizationHeaderButtons = () => {
    const { orgId } = useParams();
    const navigate = useNavigate();


    const {
        organization: storeOrganization,
        setOrganization
    } = useOrganizationStore()

    /* 
    * Delete Organization
    */
    const { mutateAsync: deleteOrganizationAsync, isPending: isPendingDeleteOrganization } = useDeleteOrganization();
    const handleDeleteOrganization = () => {
        deleteOrganizationAsync({ orgId: storeOrganization?.id as number })
            .then(() => {
                SuccessPopup("Organization deleted successfully")
                setOrganization(null)
                navigate('/welcome')
            })
            .catch((err) => {
                console.error(err)
                ErrorPopup("Error deleting organization")
            })
    }

    const { isEditOrganizationModalOpen, setEditOrganizationModalOpen } = useIsEditOrganizationModalOpen(false);
    const { mutateAsync: updateOrganization } = useUpdateOrganization();
    const handleEditOrganization = (data: OrganizationFormSchemaType) => {
        console.log(data)
        if (data) {
            updateOrganization({ id: storeOrganization?.id as number, ...data })
                .then(() => {
                    SuccessPopup("Organization updated successfully")
                    setEditOrganizationModalOpen(false)
                })
                .catch((err) => {
                    console.error(err)
                    ErrorPopup("Error updating organization")
                })
        }
    }

    if (!orgId || !storeOrganization) {
        return null;
    }

    return (
        <div>
            {isPendingDeleteOrganization && (<LoaderWithBackdrop />)}
            <EditOrganizationModal
                open={isEditOrganizationModalOpen}
                onOpenChange={setEditOrganizationModalOpen}
                onSubmitHandler={handleEditOrganization}
            />
            {/* <TooltipProvider> */}
            <Menubar>
                <CheckOrganizationAccess
                    crm_create crm_update
                    crm_delete
                    crm_config_create
                    crm_project_update
                    crm_project_delete
                    crm_project_create
                    crm_config_update
                    crm_config_delete
                    any
                >
                    <MenubarMenu>
                        <MenubarTrigger className='flex gap-2'>
                            <UnderlineLink to={`/organization/${orgId}/manage-organization`}>
                                <SquareKanban size={16} /> <span className="hidden lg:inline">Manage Organization</span>
                            </UnderlineLink>
                        </MenubarTrigger>
                    </MenubarMenu>
                </CheckOrganizationAccess>

                <CheckOrganizationAccess
                    project_create
                    project_update
                    project_delete
                    any
                >
                    <MenubarMenu>
                        <MenubarTrigger asChild className='flex gap-2'>
                            <UnderlineLink to={`/organization/${orgId}/manage-organization/projects`}>
                                <FileChartColumn size={16} /> <span className="hidden lg:inline">Manage <OrganizationTypeWordingResolver capitalize plural /></span>
                            </UnderlineLink>
                        </MenubarTrigger>
                    </MenubarMenu>
                </CheckOrganizationAccess>
                <CheckOrganizationAccess
                    survey_create
                    survey_update
                    survey_delete
                    any
                >
                    <MenubarMenu>
                        <MenubarTrigger asChild className='flex gap-2'>
                            <UnderlineLink to={`/organization/${orgId}/manage-organization/survey-bank`}>
                                <ClipboardPen size={16} /> <span className="hidden lg:inline">Survey Bank</span>
                            </UnderlineLink>
                        </MenubarTrigger>
                    </MenubarMenu>
                </CheckOrganizationAccess>
                <CheckOrganizationAccess
                    question_create
                    question_update
                    question_delete
                    any
                >
                    <MenubarMenu>
                        <MenubarTrigger asChild className='flex gap-2'>
                            <UnderlineLink to={`/organization/${orgId}/manage-organization/questions-bank`}>
                                <FileQuestionMark size={16} /> <span className="hidden lg:inline">Question Bank</span>
                            </UnderlineLink>
                        </MenubarTrigger>
                    </MenubarMenu>
                </CheckOrganizationAccess>
                <CheckOrganizationAccess
                    roles={['organization_member', 'organization_owner']}
                >
                    <MenubarMenu>
                        <MenubarTrigger asChild className='flex gap-2'>
                            <UnderlineLink to={`/organization/${orgId}/manage-organization/organization-members`}>
                                <MailPlus size={16} /> <span className="hidden lg:inline">Members</span>
                            </UnderlineLink>
                        </MenubarTrigger>
                    </MenubarMenu>
                </CheckOrganizationAccess>
                <CheckOrganizationAccess
                    organization_delete
                    organization_update
                >

                    <MenubarMenu>
                        <MenubarTrigger asChild className='flex gap-2'>
                            <div className="text-xs">
                                <Settings size={16} /> <span className="hidden lg:inline">Options</span>
                            </div>
                        </MenubarTrigger>
                        <MenubarContent align='end'>
                            <CheckOrganizationAccess
                                organization_update
                            >
                                <MenubarItem asChild className='flex justify-start w-full gap-2 mt-1' >
                                    <Button className="text-xs" size='sm' variant='ghost' onClick={() => setEditOrganizationModalOpen(true)}>
                                        <Pencil size={16} /> Edit
                                    </Button>
                                </MenubarItem>
                            </CheckOrganizationAccess>
                            <CheckOrganizationAccess
                                organization_delete
                            >
                                <ConfirmByTypingTextDialog
                                    onSubmit={() => {
                                        handleDeleteOrganization()
                                    }}
                                    confirmText={storeOrganization.name!}
                                    dialogTrigger={
                                        <Button className="flex justify-start w-full gap-2 px-2 mt-1 text-xs" size='sm' variant='destructive'>
                                            <Trash2 size={16} /> Delete
                                        </Button>
                                    }
                                />
                            </CheckOrganizationAccess>
                        </MenubarContent>
                    </MenubarMenu>
                </CheckOrganizationAccess>
            </Menubar>
        </div >
    )
}
